import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getChatHistory } from '../api';
import { stripPersonaTags } from '../lib/strip-persona-tags';
import type { ChatHistoryMessage } from '../types';
import { Spinner } from '../components/Spinner';

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Read-only feed over the context='general' lane (Brief C reading 2 —
// coaching is project-scoped, so this lane no longer carries interactive
// chat). Mostly the two toggle handlers' sign-off/re-engagement notices
// (profile.ts), but runCoach() also still lands rows here via the checkin
// route for users with no active project — role filtered to 'assistant'
// so a synthetic checkin-prompt row never renders as if it were a note.
// GET /chat/history is unchanged — this is a new consumer of the same
// repurposed read, not a new endpoint.
export function CoachNotes() {
  const navigate = useNavigate();
  const [notes, setNotes] = useState<ChatHistoryMessage[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getChatHistory()
      .then((history) => {
        if (cancelled) return;
        const assistantOnly = history.filter((n) => n.role === 'assistant');
        setNotes([...assistantOnly].reverse()); // most recent first — this is a feed, not a conversation
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="shell">
      <header className="rise" style={{ marginBottom: 8 }}>
        <p className="eyebrow">Coach</p>
        <h1 className="h1">Notes</h1>
      </header>

      {error ? <div className="errorbar rise">{error}</div> : null}

      {!notes && !error ? (
        <div className="center" style={{ marginTop: 40 }}>
          <Spinner />
        </div>
      ) : null}

      {notes && notes.length === 0 ? (
        <div className="card card--elevated rise" style={{ marginTop: 16 }}>
          <p className="muted" style={{ lineHeight: 1.6, fontSize: 15, margin: 0 }}>
            No notes yet. You'll see updates here when coaching is turned on or off.
          </p>
        </div>
      ) : null}

      {notes && notes.length > 0 ? (
        <div className="stack rise" style={{ marginTop: 16, gap: 12 }}>
          {notes.map((n) => (
            <div key={n.id} className="card">
              <p className="eyebrow">{fmtDate(n.created_at)}</p>
              <p style={{ marginTop: 8, lineHeight: 1.55 }}>{stripPersonaTags(n.content)}</p>
            </div>
          ))}
        </div>
      ) : null}

      <div className="row rise" style={{ marginTop: 16 }}>
        <button type="button" className="btn btn--ghost" onClick={() => navigate('/profile')}>
          Profile
        </button>
      </div>
    </div>
  );
}
